import { MethodType } from '../../components/AccountDrawer/Billing/PaymentMethods';
import { DiscountPeriod } from '../admin-discounts/types';
import { Address, BaseCompany, Contact, DeliveryDetailsInUser } from '../company/types';
import { CustomerConnectionStatus } from '../customer/types';
import { FreightRule } from '../freight-rule/types';
import { PaginationResult, SuccessResponse } from '../index';
import { FinishOrderOption } from '../order/types';
import { PaymentMethod } from '../payment-method/types';
import { PriceList } from '../price-list/types';
import { Category } from '../product/types';
import { CustomPermission, TeammatePermission } from '../teammates/types';

export interface BaseUser {
  id: number;
  email: string;
  abn_number: string | null;
  code: string | null;
  first_name: string;
  last_name: string;
  full_name: string;
  picture_url: string;
  phone_number: string | null;
  slug: string;
  created_at: string;
  company_addresses?: Address[];
  on_boarded: boolean;
  teammate_onboarded: boolean;
  subscription_type: SubscriptionPlanEnum;
  is_admin: boolean;
  is_sa: boolean;
  is_teammate: boolean;
  account_email?: string;
  verified: boolean;
  profit_margin: boolean;
  auto_verify: boolean;
  email_verified: boolean;
  demo_exited: boolean;
  hidden_column?: HiddenColumn | null;
  supplier_blocked?: boolean;
  onboarding_tooltips_shown?: boolean;
  show_prices: boolean;
  holidays_delivery: boolean;
  daily_order_mail: boolean;
  holidays?: HolidayDate[];
  deleted_at: string | null;
  autofill_once?: boolean;
  pos_set_up?: boolean;
  auto_pos_fetch?: boolean;
  country_id: number | null;
  last_sync_details?: null | {
    id: number;
    created_at: string;
    sales_count: number;
    sales_total: string;
    updated_at: string;
    user_id: number;
    waste_count: number;
  };
  pos_sync_time?: string | null;
  country_data_for?: string;
  sign_up_status?: 'menu_costing' | 'restaurent_pro_suite';
  payment_method_type?: MethodType | null;
  permission: TeammatePermission | null;
  custom_permission?: CustomPermission | null;
  restaurant_onboarding_level?: 'Setup profile' | 'Add suppliers' | 'complete';
  same_product_popup: boolean;
  cut_off_time_notifications: boolean;
  inventory_trigger_notifications: boolean;
  tax_name: string;
  tax_value: number;
  has_xero_connection: boolean;
  has_myob_connection: boolean;
  has_qb_connection: boolean;
  accepted_suppliers_count: number;
  trial_days_left: number | null;
  subscription_renewal_date: string;
  subscription_interval: string;
  is_supplier?: boolean;
  has_payment_error?: boolean;
  orders_this_month: number;
  country_code: string;
  has_accounting_connection: boolean;
  enable_sso: boolean;
  linked_restaurants: LinkedUser[];
  linked_suppliers: LinkedUser[];
}

export interface LinkedUser {
  id: number;
  name: string;
  location: string;
  picture_url: string;
  email: string;
}

export interface SupplierUser extends BaseUser {
  company: BaseCompany;
  delivery_detail: DeliveryDetailsInUser;
  next_day_delivery_day: string;
  delivery_radiuses: DeliveryRadius[];
  categories: Category[];
  delivery_windows: DeliveryWindows[];
  company_addresses: Address[];
  company_contacts?: Contact[];
  finish_type: FinishOrderOption;
  company_id: number;
  stripe_user_id: string | null;
  delivery_fee_by_day: {
    id: number;
    apply_delivery_fee: boolean;
  } & Record<string, number>;
  manual_supplier: boolean;
  locked_supplier: boolean;
  email_set: boolean;
  display_stock: boolean;
  added_by: number | null;
  default_freight_rule: FreightRule;
  connection_detail: ConnectionDetail | null;
}

export interface ConnectionRequest {
  id: number;
  status: CustomerConnectionStatus;
  company_name: string | null;
  company_logo: string | null;
  sales_to_date: number;
  last_order_date: string;
  price_list: PriceList | null;
  user_price_list: Partial<PriceList> | null;
  connection_detail: ConnectionDetail | null;
  customer_id: string | null;
  send_by: Roles;
  user: BaseUser;
  company_addresses: Address[];
  supplier: SupplierUser;
  delivery_number: string | null;
  delivery_run: string | null;
  salesperson_id: number | null;
  default_payment_method: PaymentMethod | null;
  customer_teammates: {
    id: number;
    full_name: string;
    phone_number: string;
    email: string;
    last_login_at: string;
  }[];
  connection_categories: {
    category_id: number;
    connection_request_id: number;
    id: number;
  }[];
  categories: Category[];
  restaurant_company: {
    id: number;
    name: string | null;
    picture_url: string | null;
  };
  supplier_company: {
    id: number;
    name: string | null;
    picture_url: string | null;
  };
  minimum_delivery_apply: boolean;
  created_at: string;
}

export interface CustomerListResponse extends PaginationResult {
  customers: ConnectionRequest[];
}

export enum CustomerPaymentMethod {
  account = 'on_account',
  card = 'credit_card',
  none = 'selected_none',
}

export interface ConnectionDetail {
  id: number;
  charge_delivery: boolean;
  payment_method: CustomerPaymentMethod;
  stop_credit: boolean;
  debit_credit_card: boolean;
  direct_debit: boolean;
  card_fee: boolean;
  credit_limit: number;
  credit_limit_action: string;
  delivery_days: number[];
  supplier_id: number;
  user_id: number;
  created_at: string;
  billing_address: string;
  billing_email: string;
  delivery_instructions: string;
  delivery_address: string;
  contact_number: string;
  notes: string;
  freight_rule_id: number;
  freight_rule: FreightRule;
}

export enum AutoCheckOffEnum {
  h6 = 'after_6_hour',
  h12 = 'after_12_hour',
  h24 = 'after_24_hour',
  h36 = 'after_36_hour',
  h48 = 'after_48_hour',
}

export enum AutoCheckOffReqEnum {
  h6 = 0,
  h12 = 1,
  h24 = 2,
  h36 = 3,
  h48 = 4,
}

export interface RestaurantUser extends BaseUser {
  company: BaseCompany;
  sub_recipe_video_viewed: boolean | null;
  delivery_windows: DeliveryWindows[];
  menu_costing_onboarding?: string[];
  pro_suite_onboarding?: RPSOnboardingSteps[];
  stripe_id: string | null;
  auto_check_off: AutoCheckOffEnum | null;
  incomplete_setup_self_managed_suppliers: {
    supplier_count: number;
    suppliers_data: { name: string; order_email: string; supplier_id: number }[];
  };
}

export enum RPSOnboardingSteps {
  INVITE = 'Invite your suppliers',
  MC = 'Set up menu costing',
  INVENTORY = 'Set up inventory & stock',
  AUTOMATED = 'Try automated ordering',
  ORDER = 'Start placing orders',
}

export interface UpdatePasswordRequest {
  user: {
    current_password: string;
    password: string;
    password_confirmation: string;
  };
}

export interface ContactMsgRequest {
  user: {
    help_subject: string;
    help_message: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    phone_number?: string;
  };
}

export enum Roles {
  CUSTOMER = 'restaurant',
  SUPPLIER = 'supplier',
}

export interface VenueResponse {
  addresses: {
    id: number;
    addressable_id: number;
    addressable_type: string;
    address_type: null | string;
    street_address1: null | string;
    street_address2: null | string;
    apt_number: null | string;
    city: string;
    state: string;
    postcode: string;
    country: string;
    latitude: null | string;
    longitude: null | string;
    full_address: string;
    created_at: string;
    updated_at: string;
    first_name: null | string;
    last_name: null | string;
    default_address: boolean;
  }[];
}

export interface UpdateDeliveryWindows {
  user: {
    delivery_windows_attributes: {
      id?: number;
      day: string;
      from_time: string;
      to_time: string;
    }[];
  };
}

export interface DeliveryWindows {
  id?: number;
  day: string;
  from_time: string;
  to_time: string;
}

export interface DeliveryRadius {
  id: number;
  delivery_radius: number;
  supplier_id: number;
  full_address: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface CreateAddressRequest {
  address: {
    id?: number;
    first_name?: string;
    city?: string;
    country?: string;
    full_address?: string;
    postcode?: string;
    state?: string;
    street_address1?: string;
    street_address2?: string;
    default_address?: boolean;
  };
}

export interface UpdateAddressRequest extends CreateAddressRequest {
  addressId: number;
}

export interface SubCategory {
  id: number;
  name: string;
  supplier_id: number;
}

export interface GetSubCategoriesResponse {
  sub_category: SubCategory[];
}

export interface CreateSubCategoryResponse extends SuccessResponse {
  sub_category: SubCategory;
}

export interface NotificationSettingResponse extends SuccessResponse {
  notification_setting: NotificationSetting;
}

export interface UpdateNotificationSettingRequest {
  notification_setting: Partial<NotificationSetting>;
}

export interface NotificationSetting {
  new_orders: boolean;
  connection_requests: boolean;
  transactional_emails: boolean;
  promotional: boolean;
  order_receipts_for_rs: boolean;
  transactional_emails_for_rs: boolean;
  important_emails_for_rs: boolean;
  cut_off_time_notifications: boolean;
  inventory_trigger_notifications: boolean;
}

export interface VerifySupplierResponse extends SuccessResponse {
  supplier: SupplierUser;
}

export enum SubscriptionPlanEnum {
  FREE = 'free',
  UNLIMITED = 'unlimited',
  ENTERPRISE = 'enterprise',
  NORMAL = 'normal',
  MENU = 'menu_planning',
  HOSPO = 'hospo',
  PRO_LITE = 'pro_lite',
  PRO_STANDARD = 'pro_standard',
}

export interface UpdateAdminProfile {
  showToast: boolean;
  user: {
    first_name?: string;
    last_name?: string;
    email?: string;
  };
}

export interface ProductColumn {
  brand_column?: boolean;
  unit_of_measure_column?: boolean;
  stock_column?: boolean;
  unit_size_column?: boolean;
  location_column?: boolean;
  shelf_space_column?: boolean;
  sub_category_column?: boolean;
  product_code_column?: boolean;
  your_cost_column?: boolean;
  tax_column?: boolean;
}

export interface SupplierInventoryColumn {
  description?: boolean;
  stock_on_hand?: boolean;
  stock_reserved?: boolean;
  stock_available?: boolean;
  par?: boolean;
  trigger?: boolean;
  avg_sales?: boolean;
  supply_remaining?: boolean;
  barcode?: boolean;
  location?: boolean;
  shelf?: boolean;
  place?: boolean;
  uom?: boolean;
}

export interface HiddenColumn {
  id: number;
  order_column?: {
    order_number?: boolean;
    total?: boolean;
    notes?: boolean;
    deliver_to?: boolean;
    delivery_number?: boolean;
  };
  order_detail_column?: {
    description?: boolean;
    unit?: boolean;
    quantity?: boolean;
    status?: boolean;
    price?: boolean;
    total?: boolean;
    product_code?: boolean;
    location?: boolean;
    shelf?: boolean;
    brand?: boolean;
  };
  product_column?: ProductColumn;
  supplier_inventory_column?: SupplierInventoryColumn;
}

export interface UpdateHiddenColumnsReq {
  hidden_column: Omit<HiddenColumn, 'id'>;
}

export interface UpdateColumnsResponse extends SuccessResponse {
  supplier: SupplierUser;
}

export interface HolidayDate {
  id: number;
  holiday_date: string;
}

export interface PurchaseResponse extends SuccessResponse {
  client_secret: string | null;
}

export interface PurchaseRequest {
  payment_method: string;
  first_name: string;
  last_name: string;
  email: string;
  coupon_code?: string;
  country_code: string;
  payment_interval?: string;
}

export interface PaymentRequest {
  payment_method: string;
  payment_type: string;
  payment_interval: string;
}

export interface VerifyPromoCodeRes extends SuccessResponse {
  percent_off: number | null;
  amount_off: number | null;
  discount_period: DiscountPeriod;
}
